<template>
    <div>
        <div
            v-if="chartData.rows && chartData.rows.length > 0 && !chartLoading"
            class="ChartContainer"
        >
            <v-chart
                ref="chart"
                :style="{ height: `${windowHeight * 0.39}px` }"
                :option="heatMapChartOptions"
                autoresize
                class="Chart"
            />
        </div>
        <div
            v-else-if="!chartLoading"
            :style="{ height: `${windowHeight * 0.39}px` }"
            class="flex flex-center"
        >
            <WText>
                {{ $t('visualization.customPlotPanels.attributesInfo.noData') }}
            </WText>
        </div>
    </div>
</template>
<script>
import moment from 'moment'
import { dashboardChartsMixin } from '@/mixins'
import echartsColors from '@/charts/echartsColors'

export default {
    name: 'HeatMapChart',
    mixins: [dashboardChartsMixin],
    props: {
        index: {
            type: [String, Number],
            required: true,
        },
        chartData: {
            type: Object,
            required: true,
        },
        chartCreationProps: {
            type: Object,
            required: true,
        },
        chartLoading: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['get-chart-data', 'chartLoading'],
    data() {
        return {
            ownFilters: [],
            windowHeight: window.innerHeight - 100,
            heatMapChartOptions: {
                tooltip: {
                    position: 'top',
                },
                autoresize: true,
                grid: {
                    containLabel: true,
                    top: 10,
                    bottom: 50,
                    right: 10,
                    left: 15,
                },
                xAxis: {
                    type: 'category',
                    data: [],
                    splitArea: {
                        show: true,
                    },
                    tooltip: {
                        show: true,
                    },
                    nameTruncate: {
                        maxWidth: '10px',
                    },
                    axisLabel: {
                        fontSize: '10px',
                        rotate: 45,
                        interval: 0,
                        formatter: (value) => {
                            const maxLength = 15
                            return value.length > maxLength ? `${value.slice(0, maxLength) }...` : value
                        },
                    },
                },
                yAxis: {
                    type: 'category',
                    data: [],
                    splitArea: {
                        show: true,
                    },
                    axisLabel: {
                        fontSize: '10px',
                        formatter: (value) => {
                            const maxLength = 15
                            return value.length > maxLength ? `${value.slice(0, maxLength) }...` : value
                        },
                    },
                },
                visualMap: {
                    min: 0,
                    max: 10,
                    calculable: true,
                    orient: 'horizontal',
                    left: 'center',
                    bottom: '0',
                    inRange: {
                        color: echartsColors.heatmap.range,
                    },
                },
                series: [{
                    name: 'Transitions',
                    type: 'heatmap',
                    data: [],
                    label: {
                        show: true,
                    },
                    emphasis: {
                        itemStyle: {
                            shadowBlur: 10,
                            shadowColor: 'rgba(0, 0, 0, 0.5)',
                        },
                    },
                }],
            },
        }
    },
    watch: {
        chartData: {
            handler(newVal) {
                if (newVal.rows && newVal.rows.length > 0) {
                    this.updateChartOptions(newVal)
                }
            },
            deep: true,
        },
    },
    created() {
        this.$eventBus.on(`updateSortParams-${this.index}`, this.updateSelection)
    },
    methods: {
        resetChart() {
            this.heatMapChartOptions.series[0].data = []
            this.heatMapChartOptions.xAxis.data = []
            this.heatMapChartOptions.yAxis.data = []
        },
        updateSelection(selection) {
            this.resetChart()
            this.$emit('chartLoading', true)
            const params = {
                sortBy: selection.sortBy,
                sortDirection: selection.sortDirection,
            }
            this.$emit('get-chart-data', params)
        },
        updateOwnFilters(selection) {
            this.ownFilters = selection
            this.setOwnFilters(this.ownFilters)
        },
        updateChartOptions(data) {
            const seriesData = [...new Set(data.rows.map(({ transition }) => [transition.source, transition.target]).flat())]
            const values = data.rows.map(({ value }) => value)
            const isDuration = this.chartCreationProps.sortBy !== 'FREQUENCY'

            const heatmapData = data.rows.map(({ transition, value }) => ([
                seriesData.indexOf(transition.source),
                seriesData.indexOf(transition.target),
                value,
            ]))

            this.heatMapChartOptions = {
                ...this.heatMapChartOptions,
                xAxis: {
                    ...this.heatMapChartOptions.xAxis,
                    data: seriesData,
                },
                yAxis: {
                    ...this.heatMapChartOptions.yAxis,
                    data: seriesData,
                },
                series: [{
                    ...this.heatMapChartOptions.series[0],
                    data: heatmapData,
                    label: {
                        show: true,
                        formatter: ({ value }) => (isDuration ? this.formatDuration(value[2]) : value[2]),
                    },
                }],
                visualMap: {
                    ...this.heatMapChartOptions.visualMap,
                    min: Math.min(...values),
                    max: Math.max(...values) < 2 ? 10 : Math.max(...values),
                    formatter: (value) => (isDuration ? this.formatDuration(value) : value),
                },
                tooltip: {
                    ...this.heatMapChartOptions.tooltip,
                    formatter: ({ value }) => `
                ${this.$t('visualization.dashboard.heatmap.source')}: ${seriesData[value[0]]}
                <br/>
                ${this.$t('visualization.dashboard.heatmap.target')}: ${seriesData[value[1]]}
                <br/>
                ${this.$t('visualization.dashboard.heatmap.value')}: ${(isDuration ? this.formatDuration(value[2]) : value[2])}
            `,
                },
            }
            this.$emit('chartLoading', false)
        },
        formatDuration(duration) {
            return moment.duration(duration, 'seconds').format('d[d] h[h]:m[m]:s[s]', { largest: 2, trim: false })
        },
    },
}
</script>
<style lang="scss">
.ChartContainer {
  width: 100%;
  height: 100%;
  flex-grow: 1;
 //  background-color: #fbfbfb;
  .Chart {
      width: 100%;
      min-width: 100%;
      height:270px;
  }
}
</style>
